import axios from 'axios'
import { endSession } from '@/utils/session'

const API_URL = process.env.VUE_APP_API_HOST_DOMAIN
// Images live where they were uploaded, normally the API host. Set VUE_APP_IMAGE_HOST_DOMAIN to
// show the live server's images while the API points at a local backend without its uploads.
const IMAGE_URL = process.env.VUE_APP_IMAGE_HOST_DOMAIN || API_URL
const headers = {
    'Content-Type': 'application/json; charset=utf-8'
}
const formDataHeaders = {
    'Content-Type': 'multipart/form-data'
}

// The backend reports most failures as HTTP 200 with a body like {"400": "member edit error"},
// so a resolved request is not necessarily a successful one.
const ERROR_KEYS = ['400', '404', '500', 'error']

function bodyErrorMessage(data) {
    if (!data || typeof data !== 'object' || Array.isArray(data) || data instanceof Blob) {
        return null
    }
    const key = ERROR_KEYS.find(errorKey => data[errorKey])
    if (!key) {
        return null
    }
    let message = typeof data[key] === 'string' ? data[key] : 'Request failed'
    if (Array.isArray(data.errors) && data.errors.length > 0) {
        message += ': ' + data.errors.map(error => error.field ? `${error.field} – ${error.message}` : error.message).join(', ')
    }
    return message.charAt(0).toUpperCase() + message.slice(1)
}

// expired, revoked or invalid token: sign in again (a failed login itself also answers 401)
function handleUnauthorized(urlPath) {
    if (urlPath !== 'login') {
        endSession()
    }
}

export default {
    apiUrl: API_URL,
    post(urlPath, data) {
        return this.request(urlPath, 'POST', headers, data)
    },
    put(urlPath, data) {
        return this.request(urlPath, 'PUT', headers, data)
    },
    get(urlPath) {
        return this.request(urlPath, 'GET', headers, null)
    },
    delete(urlPath) {
        return this.request(urlPath, 'DELETE', headers, null)
    },
    uploadFile(urlPath, formData) {
        return this.request(urlPath, 'POST', formDataHeaders, formData)
    },
    download(urlPath, data = null, method = 'POST') {
        return this.request(urlPath, method, headers, data, { responseType: 'blob' })
    },
    // page, per_page, search and column sort for the GET list endpoints
    listPath(path, { page, perPage, filters = {}, sortOption, order }) {
        const params = new URLSearchParams({ page: page || 1, per_page: perPage || 10 })
        if (filters.search) {
            params.set('search', filters.search)
        }
        if (sortOption) {
            params.set('sortOption', sortOption)
            params.set('order', order || 'ASC')
        }
        return `${path}?${params.toString()}`
    },
    imageUrl(image) {
        return image && image.imageName ? `${IMAGE_URL}/uploads/images/${image.imageName}` : null
    },
    request(urlPath, method, headers, data, options = {}) {
        method = method || 'POST'
        data = data || {}
        const apiToken = localStorage.getItem('api-token')
        if (apiToken) {
            headers = {
                ...headers,
                'Authorization': 'Bearer ' + apiToken
            }
        }

        return axios({
            url: API_URL + '/' + urlPath,
            data: data,
            method: method,
            headers: headers,
            ...options
        }).then(response => {
            const message = bodyErrorMessage(response.data)
            if (message) {
                const error = new Error(message)
                error.isApiError = true
                error.response = response
                throw error
            }
            return response
        }).catch(error => {
            if (!error.isApiError && error.response && error.response.status === 401) {
                handleUnauthorized(urlPath)
            }
            throw error
        })
    }
}
